// Shared bounded jittered-backoff retry for transient API failures.
//
// The QA env's worker Lambda runs at a 10-concurrency cap, so under the load of
// a multi-file submit (each upload also triggers async extraction) the API
// Gateway sheds requests with a 503 BEFORE they reach the Lambda. Without retry
// a single 503 on one of N upload calls aborts the whole batch + submit (the
// failure that lost 22 of 39 files in QA). A couple of backoff retries ride out
// a brief throttle window. Used by both the multipart part path and the
// single-file upload calls.
export const RETRYABLE_STATUSES = new Set([429, 500, 502, 503, 504]);

// Retryable when it's a transient network error (fetch rejects with TypeError
// "Failed to fetch") or carries a retryable HTTP status (PostJsonError and the
// S3-PUT error both expose `.status`). An AbortError, a 4xx (auth/expired/bad
// request), or a malformed-response error is NOT retried.
export const isRetryableError = (err: unknown): boolean => {
  if (err instanceof TypeError) return true; // network-level fetch failure
  const status = (err as { status?: number } | null)?.status;
  return typeof status === "number" && RETRYABLE_STATUSES.has(status);
};

export const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      reject(new DOMException("Aborted", "AbortError"));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort);
  });

// Jittered exponential backoff: baseMs, 2×, 4×, …, each ×[0.5,1.5).
const backoffMs = (attempt: number, baseMs: number): number =>
  baseMs * 2 ** (attempt - 1) * (0.5 + Math.random());

// Run `fn` with bounded jittered-backoff retry on transient failures, bailing
// out immediately on a non-retryable error or an aborted signal. `maxAttempts`
// is total attempts (1 initial + N retries).
export const withTransientRetry = async <T>(
  fn: () => Promise<T>,
  {
    maxAttempts = 4,
    baseMs = 300,
    signal,
  }: { maxAttempts?: number; baseMs?: number; signal?: AbortSignal } = {},
): Promise<T> => {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (attempt === maxAttempts || !isRetryableError(err)) throw err;
      await sleep(backoffMs(attempt, baseMs), signal);
    }
  }
  throw lastErr;
};
